/**
 * Productivity Calculator — formula-based GPS prediction.
 * ─────────────────────────────────────────────────────────────────────────────
 * Two modes:
 *   1. Predict from master  — pick (count, yarn_type), pull defaults, optional
 *                              overrides for RPM/TM/OEE, see GPS + shift kg +
 *                              daily kg + variance vs target + reverse calcs.
 *   2. Ad-hoc calculate     — enter any RPM/TM/OEE/count, get GPS + production
 *                              targets. Useful for "what would 21500 RPM give us?"
 *
 * The formulas (from the productivity spec):
 *   TPI = TM × √Count
 *   GPS = (7.2 × RPM × OEE) / (TPI × Count)
 */
import { useState } from 'react'
import {
  Alert, App, Button, Card, Col, Divider, Empty, InputNumber, Row,
  Select, Space, Statistic, Table, Tag, Typography, Tabs,
} from 'antd'
import {
  CalculatorOutlined, ExperimentOutlined, ThunderboltOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

const YARN_TYPES = ['CARDED', 'CARDED_COMPACT', 'COMPACT', 'SIRO', 'COMBED', 'COMBED_COMPACT']
const COUNTS = [8, 10, 12, 16, 20, 24, 30, 32, 34, 40, 45, 50, 60, 70, 80, 90, 100, 110, 120]

interface PredictResponse {
  found: boolean
  count_ne: string | null
  yarn_type: string
  commercial_status: string | null
  basis: string | null
  inputs_effective: { actual_count?: string; rpm?: string | number; tm?: string; oee_decimal?: string } | null
  inputs_default: { actual_count?: string; rpm?: string | number; tm?: string; oee_decimal?: string } | null
  computed: { tpi?: string | null; gps?: string | null; shift_kg?: string | null; daily_kg?: string | null } | null
  reference: { achieved_gps?: string | null; target_gps?: string | null } | null
  variance: {
    actual_gps?: string | null; calculated_gps?: string | null; target_gps?: string | null
    variance_vs_calculated?: string | null; variance_vs_target?: string | null
    production_loss_kg_per_shift?: string | null; production_loss_kg_per_day?: string | null
  } | null
  reverse_calcs_to_target: { required_rpm?: string | null; required_tm?: string | null; required_oee?: string | null } | null
  message?: string | null
}

interface CalcResponse {
  tpi: string | null
  gps: string | null
  shift_kg: string | null
  daily_kg: string | null
  required_rpm: string | null
  required_tm: string | null
  required_oee: string | null
}

// ─────────────────────────────────────────────────────────────────────────
// Predict (from master)
// ─────────────────────────────────────────────────────────────────────────
function PredictTab() {
  const { plantId } = usePlantId()
  const [count, setCount] = useState<number>(30)
  const [yarn, setYarn] = useState<string>('CARDED_COMPACT')
  const [rpmOverride, setRpmOverride] = useState<number | null>(null)
  const [tmOverride, setTmOverride] = useState<number | null>(null)
  const [oeeOverride, setOeeOverride] = useState<number | null>(null)
  const [spindles, setSpindles] = useState<number>(1824)
  const [shifts, setShifts] = useState<number>(3)

  const { data, isLoading, error } = useQuery<PredictResponse>({
    queryKey: ['productivity-predict', plantId, count, yarn, rpmOverride, tmOverride, oeeOverride, spindles, shifts],
    queryFn: () =>
      apiClient.get('/productivity/predict', {
        params: {
          plant_id: plantId, count_ne: count, yarn_type: yarn,
          rpm: rpmOverride || undefined, tm: tmOverride || undefined,
          oee_decimal: oeeOverride || undefined,
          spindles, shifts_per_day: shifts,
        },
      }).then(r => r.data),
    enabled: !!plantId,
    retry: false,
  })

  const errorMsg = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail

  const inputs = data?.inputs_effective
  const computed = data?.computed
  const variance = data?.variance
  const reverse = data?.reverse_calcs_to_target

  return (
    <div>
      {/* Filters */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col xs={24} sm={6}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Count (Ne)</Text>
            <Select style={{ width: '100%' }} value={count} onChange={setCount}
              options={COUNTS.map(c => ({ value: c, label: `${c}s` }))} />
          </Col>
          <Col xs={24} sm={6}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Yarn Type</Text>
            <Select style={{ width: '100%' }} value={yarn} onChange={setYarn}
              options={YARN_TYPES.map(t => ({ value: t, label: t.replace(/_/g, ' ') }))} />
          </Col>
          <Col xs={12} sm={4}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Spindles</Text>
            <InputNumber style={{ width: '100%' }} value={spindles} onChange={v => setSpindles(v ?? 0)} min={0} />
          </Col>
          <Col xs={12} sm={4}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Shifts/day</Text>
            <InputNumber style={{ width: '100%' }} value={shifts} onChange={v => setShifts(v ?? 3)} min={1} max={4} />
          </Col>
        </Row>
        <Divider orientation="left" style={{ fontSize: 11, color: '#888' }}>
          Optional overrides — leave blank to use master defaults
        </Divider>
        <Row gutter={12}>
          <Col xs={8}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>RPM</Text>
            <InputNumber style={{ width: '100%' }} value={rpmOverride}
              onChange={v => setRpmOverride(v ?? null)} placeholder="default" />
          </Col>
          <Col xs={8}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>TM</Text>
            <InputNumber style={{ width: '100%' }} value={tmOverride} step={0.1}
              onChange={v => setTmOverride(v ?? null)} placeholder="default" />
          </Col>
          <Col xs={8}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>OEE (decimal)</Text>
            <InputNumber style={{ width: '100%' }} value={oeeOverride} step={0.01} max={1} min={0}
              onChange={v => setOeeOverride(v ?? null)} placeholder="default" />
          </Col>
        </Row>
      </Card>

      {!plantId && <Empty description="Select a plant first" />}
      {isLoading && <Card loading />}
      {errorMsg && <Alert type="warning" showIcon message={errorMsg} />}

      {data && data.found && (
        <>
          {/* Status banner */}
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={
              <Space wrap>
                <Tag color="blue">{data.commercial_status}</Tag>
                <Text>{data.basis}</Text>
                {inputs?.rpm && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Effective: {inputs.actual_count}s · {inputs.rpm} rpm · TM {inputs.tm} · OEE {Number(inputs.oee_decimal ?? 0) * 100}%
                  </Text>
                )}
              </Space>
            } />

          {/* Computed */}
          <Row gutter={12} style={{ marginBottom: 12 }}>
            <Col xs={6}>
              <Card size="small">
                <Statistic title="TPI" value={computed?.tpi ?? '—'} precision={2}
                  valueStyle={{ fontSize: 22, color: '#1677ff' }} />
              </Card>
            </Col>
            <Col xs={6}>
              <Card size="small" style={{ borderTop: '3px solid #faad14' }}>
                <Statistic title="GPS (g/sp/8h)" value={computed?.gps ?? '—'} precision={2}
                  valueStyle={{ fontSize: 22, fontWeight: 700, color: '#faad14' }} />
              </Card>
            </Col>
            <Col xs={6}>
              <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
                <Statistic title="Shift kg" value={computed?.shift_kg ?? '—'} precision={1} suffix="kg"
                  valueStyle={{ fontSize: 22, color: '#52c41a' }} />
              </Card>
            </Col>
            <Col xs={6}>
              <Card size="small" style={{ borderTop: '3px solid #722ed1' }}>
                <Statistic title="Daily kg" value={computed?.daily_kg ?? '—'} precision={1} suffix="kg"
                  valueStyle={{ fontSize: 22, fontWeight: 700, color: '#722ed1' }} />
              </Card>
            </Col>
          </Row>

          {/* Reference + variance */}
          {data.reference?.target_gps && (
            <Card size="small" title="Reference + Variance" style={{ marginBottom: 12 }}>
              <Row gutter={12}>
                <Col xs={6}>
                  <Statistic title="Achieved GPS" value={data.reference?.achieved_gps ?? '—'} precision={2}
                    valueStyle={{ fontSize: 16 }} />
                </Col>
                <Col xs={6}>
                  <Statistic title="Target GPS" value={data.reference?.target_gps ?? '—'} precision={2}
                    valueStyle={{ fontSize: 16 }} />
                </Col>
                <Col xs={6}>
                  <Statistic title="Loss / shift" value={variance?.production_loss_kg_per_shift ?? '—'}
                    suffix="kg" precision={1}
                    valueStyle={{ fontSize: 16, color: Number(variance?.production_loss_kg_per_shift ?? 0) > 0 ? '#ff4d4f' : '#52c41a' }} />
                </Col>
                <Col xs={6}>
                  <Statistic title="Loss / day" value={variance?.production_loss_kg_per_day ?? '—'}
                    suffix="kg" precision={1}
                    valueStyle={{ fontSize: 16, color: Number(variance?.production_loss_kg_per_day ?? 0) > 0 ? '#ff4d4f' : '#52c41a' }} />
                </Col>
              </Row>
            </Card>
          )}

          {/* Reverse calcs */}
          {reverse?.required_rpm && (
            <Card size="small" title="What's needed to hit target">
              <Row gutter={12}>
                <Col xs={8}>
                  <Statistic title="Required RPM" value={reverse.required_rpm} precision={0}
                    valueStyle={{ fontSize: 16 }} />
                </Col>
                <Col xs={8}>
                  <Statistic title="Required TM" value={reverse.required_tm ?? '—'} precision={2}
                    valueStyle={{ fontSize: 16 }} />
                </Col>
                <Col xs={8}>
                  <Statistic title="Required OEE" value={reverse.required_oee ? `${(Number(reverse.required_oee) * 100).toFixed(2)}%` : '—'}
                    valueStyle={{ fontSize: 16 }} />
                </Col>
              </Row>
            </Card>
          )}
        </>
      )}

      {data && !data.found && (
        <Alert type="warning" showIcon
          message="No productivity row for this combination."
          description={data.message} />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Ad-hoc calculator (no master row needed)
// ─────────────────────────────────────────────────────────────────────────
function AdHocTab() {
  const { message } = App.useApp()
  const [count, setCount] = useState<number>(30.3)
  const [rpm, setRpm] = useState<number>(20500)
  const [tm, setTm] = useState<number>(3.8)
  const [oee, setOee] = useState<number>(0.94)
  const [spindles, setSpindles] = useState<number>(1824)
  const [shifts, setShifts] = useState<number>(3)
  const [target, setTarget] = useState<number | null>(null)
  const [result, setResult] = useState<CalcResponse | null>(null)
  const [loading, setLoading] = useState(false)

  const calc = async () => {
    setLoading(true)
    try {
      const r = await apiClient.post('/productivity/calculate', {
        actual_count: count, rpm, tm, oee_decimal: oee,
        spindles, shifts_per_day: shifts, hours_per_shift: 8,
        target_gps: target || undefined,
      })
      setResult(r.data)
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Calculation failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={[12, 8]}>
          <Col xs={12} sm={4}>
            <Text type="secondary" style={{ fontSize: 11 }}>Actual Count</Text>
            <InputNumber style={{ width: '100%' }} value={count} step={0.1}
              onChange={v => setCount(v ?? 30)} min={0.1} />
          </Col>
          <Col xs={12} sm={4}>
            <Text type="secondary" style={{ fontSize: 11 }}>RPM</Text>
            <InputNumber style={{ width: '100%' }} value={rpm}
              onChange={v => setRpm(v ?? 0)} min={0} />
          </Col>
          <Col xs={12} sm={4}>
            <Text type="secondary" style={{ fontSize: 11 }}>TM</Text>
            <InputNumber style={{ width: '100%' }} value={tm} step={0.05}
              onChange={v => setTm(v ?? 0)} min={0} />
          </Col>
          <Col xs={12} sm={4}>
            <Text type="secondary" style={{ fontSize: 11 }}>OEE (decimal)</Text>
            <InputNumber style={{ width: '100%' }} value={oee} step={0.01} max={1} min={0}
              onChange={v => setOee(v ?? 0)} />
          </Col>
          <Col xs={12} sm={4}>
            <Text type="secondary" style={{ fontSize: 11 }}>Spindles</Text>
            <InputNumber style={{ width: '100%' }} value={spindles}
              onChange={v => setSpindles(v ?? 0)} min={0} />
          </Col>
          <Col xs={12} sm={4}>
            <Text type="secondary" style={{ fontSize: 11 }}>Target GPS (opt.)</Text>
            <InputNumber style={{ width: '100%' }} value={target}
              onChange={v => setTarget(v ?? null)} min={0} placeholder="for reverse calcs" />
          </Col>
        </Row>
        <Divider style={{ margin: '12px 0' }} />
        <Button type="primary" icon={<CalculatorOutlined />} loading={loading} onClick={calc}>
          Calculate
        </Button>
      </Card>

      {result && (
        <>
          <Row gutter={12} style={{ marginBottom: 12 }}>
            <Col xs={6}>
              <Card size="small">
                <Statistic title="TPI" value={result.tpi ?? '—'} precision={2}
                  valueStyle={{ fontSize: 20, color: '#1677ff' }} />
              </Card>
            </Col>
            <Col xs={6}>
              <Card size="small" style={{ borderTop: '3px solid #faad14' }}>
                <Statistic title="GPS" value={result.gps ?? '—'} precision={2}
                  valueStyle={{ fontSize: 20, fontWeight: 700, color: '#faad14' }} />
              </Card>
            </Col>
            <Col xs={6}>
              <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
                <Statistic title="Shift kg" value={result.shift_kg ?? '—'} precision={1}
                  valueStyle={{ fontSize: 20, color: '#52c41a' }} />
              </Card>
            </Col>
            <Col xs={6}>
              <Card size="small" style={{ borderTop: '3px solid #722ed1' }}>
                <Statistic title="Daily kg" value={result.daily_kg ?? '—'} precision={1}
                  valueStyle={{ fontSize: 20, fontWeight: 700, color: '#722ed1' }} />
              </Card>
            </Col>
          </Row>

          {(result.required_rpm || result.required_tm || result.required_oee) && (
            <Card size="small" title="Reverse calcs (to hit target GPS)">
              <Row gutter={12}>
                <Col xs={8}>
                  <Statistic title="Required RPM" value={result.required_rpm ?? '—'} precision={0}
                    valueStyle={{ fontSize: 16 }} />
                </Col>
                <Col xs={8}>
                  <Statistic title="Required TM" value={result.required_tm ?? '—'} precision={2}
                    valueStyle={{ fontSize: 16 }} />
                </Col>
                <Col xs={8}>
                  <Statistic title="Required OEE"
                    value={result.required_oee ? `${(Number(result.required_oee) * 100).toFixed(1)}%` : '—'}
                    valueStyle={{ fontSize: 16 }} />
                </Col>
              </Row>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
export default function ProductivityCalculatorPage() {
  return (
    <App>
      <PageHeader
        title="Productivity Calculator"
        subtitle="GPS = (7.2 × RPM × OEE) / (TM × √Count × Count) — formula-based, never hardcoded"
      />
      <Tabs
        items={[
          {
            key: 'predict',
            label: <span><ThunderboltOutlined /> Predict from Master</span>,
            children: <PredictTab />,
          },
          {
            key: 'adhoc',
            label: <span><ExperimentOutlined /> Ad-hoc Calculator</span>,
            children: <AdHocTab />,
          },
        ]}
      />
    </App>
  )
}
